// routes/makePaths.ts
import { ROOTS, BASES, ProviderType } from "./routeTypes";
type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends (...args: any) => any
    ? T[K] // keep function types intact
    : T[K] extends object
    ? DeepPartial<T[K]>
    : T[K];
};

function deepMerge<T>(base: T, patch?: DeepPartial<T>): T {
  if (!patch) return base;
  const out: any = Array.isArray(base) ? [...(base as any)] : { ...base };
  for (const k in patch) {
    const v = (patch as any)[k];
    if (v === undefined) continue;
    if (
      typeof v === "object" &&
      v !== null &&
      !Array.isArray(v) &&
      typeof (base as any)[k] === "object"
    ) {
      out[k] = deepMerge((base as any)[k], v as any);
    } else {
      out[k] = v;
    }
  }
  return out;
}
type SettingsShape = {
  root: string;
  global: string;
  discounts: string;
  //   pricing: string;
  //   venue: { root: string; new: string; edit: (id: number) => string };
  //   training: { root: string; new: string; edit: (id: number) => string };
  //   blocking: { root: string; new: string; edit: (id: string) => string };
  //   passestickets: { root: string; new: string; edit: (id: number) => string };
};

const template = (base: string) => {
  const prefix = base ? `/${base}` : ""; // no trailing slash if base is ""
  const dash = (p: string) => `${ROOTS.DASHBOARD}${p}`;
  const settings: SettingsShape = {
    root: dash(`/settings`), // facility default: no prefix
    global: dash(`/settings/global`),
    discounts: dash(`${prefix}/settings/discounts`),
    // pricing: dash(`/settings/pricing`),
    // venue: {
    //   root: dash(`/settings/venue`),
    //   new: dash(`/settings/venue/new`),
    //   edit: (id) => dash(`/settings/venue/${id}/edit`),
    // },
  };
  return {
    // home: dash(`${prefix}/home`),
    home: dash(``),

    profile: {
      root: dash(`${prefix}/profile`),
      data: dash(`${prefix}/profile/data`),
      images: dash(`/profile/images`), // deliberately no prefix
      sports: dash(`/profile/sports`),
      billing_infos: dash(`/profile/billing-infos`),
    },
    settings,
    adminuser_management: dash(`/adminuser-management`),
    payment_management: dash(`/payment-management`),
    analytics: dash(`/analytics`),
    post: {
      root: dash(`/post`),
      new: dash(`/post/new`),
    },
    players_and_teams: {
      root: dash(`${prefix}/players-and-teams`),
      players: dash(`${prefix}/players-and-teams/players`),
      teams: dash(`${prefix}/players-and-teams/teams`),
    },

    tournaments: `${ROOTS.DASHBOARD}${prefix}/tournaments`,
    livestreaming: `${ROOTS.DASHBOARD}${prefix}/livestreaming`,
    tournament: {
      profile: (id: string) =>
        `${ROOTS.DASHBOARD}${prefix}/tournament/${id}/profile`,
      schedule: (id: string) =>
        `${ROOTS.DASHBOARD}${prefix}/tournament/${id}/schedule`,
      results: (id: string) =>
        `${ROOTS.DASHBOARD}${prefix}/tournament/${id}/results`,
    },
    rankings: `${ROOTS.DASHBOARD}${prefix}/rankings`,
  };
};

/**
 * Optional: per-provider overrides (rare).
 * Example: organiser uses a different profile data url.
 */
const OVERRIDES: Partial<
  Record<
    ProviderType,
    Partial<ReturnType<typeof template>> & {
      profile?: Partial<ReturnType<typeof template>["profile"]>;
      tournament?: Partial<ReturnType<typeof template>["tournament"]>;
      settings?: Partial<ReturnType<typeof template>["settings"]>;
    }
  >
> = {
  organiser: {
    settings: {
      root: `${ROOTS.DASHBOARD}/organiser/settings`,
      global: `${ROOTS.DASHBOARD}/organiser/settings/global`,
      discounts: `${ROOTS.DASHBOARD}/organiser/settings/discounts`,
    },
  },
};

export type PathsShape = ReturnType<typeof template>;

/** Build full paths object for one provider base */
export function makePathsFor(base: string): PathsShape {
  return template(base);
}

/** Build a map: { organiser: PathsShape, facility: PathsShape, ... } */
export const pathsByType: Record<ProviderType, PathsShape> = (
  Object.keys(BASES) as ProviderType[]
).reduce(
  (acc, type) => {
    const built = template(BASES[type]);
    const patch = (
      OVERRIDES as Partial<Record<ProviderType, DeepPartial<PathsShape>>>
    )[type];
    acc[type] = deepMerge(built, patch);
    return acc;
  },
  {} as Record<ProviderType, PathsShape>
);
